import { Box, Divider, NumberInput, Stack, Switch, Text } from '@mantine/core';
import { checkinWeekText, defaultMidpointWeek } from '../../../utils/checkinWeeks';

export interface CheckinSettingsForm {
  introEnabled: boolean;
  introWeek: number | null;
  midpointEnabled: boolean;
  midpointWeek: number | null;
}

interface Props {
  value: CheckinSettingsForm;
  onChange: (next: CheckinSettingsForm) => void;
  /** Program length in weeks, or null when a date is missing. */
  programWeeks: number | null;
  error?: string | null;
  /** Border of the white box. Matches the session rows of the host form. */
  borderColor?: string;
  /**
   * Show the target week fields. The create wizard sets the weeks on its
   * Dates step, so its Session Configuration step shows the switches only.
   */
  showWeeks?: boolean;
  /** MED-1350: the cohort start date. Each week shows its date range, as in the wizard. */
  startDate?: Date | string | null;
}

// The "Target week" field lines up with the switch label: the sm track
// (38px) plus the label offset (spacing sm).
const WEEK_FIELD_INDENT = 'calc(38px + var(--mantine-spacing-sm))';

/** MED-1350: intro and midpoint check-in toggles with their target weeks. */
export function CheckinSettingsFields({
  value,
  onChange,
  programWeeks,
  error,
  borderColor = '#E8E6E3',
  showWeeks = true,
  startDate = null,
}: Props) {
  const toWeek = (v: string | number): number | null =>
    typeof v === 'number' ? v : null;

  return (
    <Box mt="md">
      <Divider mb="sm" />
      <Text
        size="xs"
        fw={600}
        tt="uppercase"
        c="dimmed"
        mb="sm"
        style={{ letterSpacing: '0.3px' }}
      >
        Check-ins
      </Text>
      <Box
        py={12}
        px={16}
        style={{
          backgroundColor: 'white',
          borderRadius: '6px',
          border: `1px solid ${borderColor}`,
        }}
      >
        <Stack gap="sm">
          <Switch
            label="Intro check-in"
            checked={value.introEnabled}
            onChange={e =>
              onChange({
                ...value,
                introEnabled: e.currentTarget.checked,
                introWeek: value.introWeek ?? 1,
              })
            }
          />
          {showWeeks && value.introEnabled && (
            <NumberInput
              label="Target week"
              aria-label="Intro check-in target week"
              min={1}
              allowDecimal={false}
              allowNegative={false}
              size="sm"
              w={120}
              ml={WEEK_FIELD_INDENT}
              value={value.introWeek ?? ''}
              onChange={v => onChange({ ...value, introWeek: toWeek(v) })}
              description={checkinWeekText(value.introWeek, startDate)}
              inputWrapperOrder={['label', 'input', 'description', 'error']}
              styles={{ description: { whiteSpace: 'nowrap' } }}
            />
          )}

          <Divider color="gray.2" />

          <Switch
            label="Midpoint check-in"
            checked={value.midpointEnabled}
            onChange={e =>
              onChange({
                ...value,
                midpointEnabled: e.currentTarget.checked,
                midpointWeek:
                  value.midpointWeek ?? defaultMidpointWeek(programWeeks),
              })
            }
          />
          {showWeeks && value.midpointEnabled && (
            <NumberInput
              label="Target week"
              aria-label="Midpoint check-in target week"
              min={1}
              allowDecimal={false}
              allowNegative={false}
              size="sm"
              w={120}
              ml={WEEK_FIELD_INDENT}
              value={value.midpointWeek ?? ''}
              onChange={v => onChange({ ...value, midpointWeek: toWeek(v) })}
              description={checkinWeekText(value.midpointWeek, startDate)}
              inputWrapperOrder={['label', 'input', 'description', 'error']}
              styles={{ description: { whiteSpace: 'nowrap' } }}
            />
          )}
          {/* MED-1350: with no cohort dates there is no half-way week to fill
              in, so say how the week gets its value. */}
          {showWeeks &&
            value.midpointEnabled &&
            value.midpointWeek == null &&
            programWeeks == null && (
              <Text size="xs" c="dimmed" ml={WEEK_FIELD_INDENT} mt={-8}>
                Set to half-way once cohort dates are known.
              </Text>
            )}

          <Text size="sm" c="dimmed">
            Check-ins are not paid.
          </Text>
          {error && (
            <Text size="sm" c="red" role="alert">
              {error}
            </Text>
          )}
        </Stack>
      </Box>
    </Box>
  );
}
